import { Link as RouterLink } from 'react-router-dom'
import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import { QueryStatus, useQuery } from '@owl-nest/hooks'
import { Either } from '@owl-nest/monad'
import * as plume from '@ulule/owl-kit-components/next'
import { t } from '@owl-nest/localize'
import * as api from '@owl-nest/api-client/latest'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'
import * as duvet from '@ulule/duvet'
import * as model from '@owl-nest/models'

import { Project } from '../../../../models/project'
import { PATHS } from '../../../../paths'
import { RootState } from '../../../../../reducers'
import * as Styles from '../../style'
import { useNavigation } from '../../../../hooks/useNavigation'
import { useDisplayMode } from '../../../../hooks/useDisplayMode'
import { SidebarNewsCTA, SidebarRewardsCTA, SidebarDonationCTA } from '../../components/Sidebar'
import { TRACKING_SOURCE } from '../../utils/trackerEnums'
import { FollowProjectButton } from '../../components/FollowProjectButton'

type NewsPermalinkProps = {
  news: api.News<true, true>
  id: number
}

export function NewsPermalink({ news, id }: NewsPermalinkProps): React.ReactElement<NewsPermalinkProps> {
  const project = Redux.useSelector<RootState, Project>((state) => state.application.project.data)
  const auth = useAuth()
  const navigation = useNavigation()
  const displayMode = useDisplayMode(project)
  const isPreview = displayMode === 'preview'

  const isLoggedIn = auth.type === 'loggedin'
  const [query, response] = useQuery(async () => {
    if (news?.id !== id || news === undefined) {
      const result = await api.api.get.news({
        urlParams: { newsId: String(id) },
        withToken: isLoggedIn,
      })
      return result.next((success) => success.body)
    }

    return Either.right(news)
  })

  React.useEffect(() => {
    if (news?.id !== id) {
      query()
    }
  }, [id])

  if (response.status === QueryStatus.PRISTINE) {
    query()
  }

  if (response.status !== QueryStatus.SUCCESS) {
    return (
      <Styles.Main>
        <Styles.Wrapper>
          <Styles.Content>
            <plume.Spinner />
          </Styles.Content>
        </Styles.Wrapper>
      </Styles.Main>
    )
  }

  news = response.data || news

  const isUserStaff = auth.type === 'loggedin' && auth.user.is_staff
  const isUserAllowed = Boolean(news.permissions.self?.includes('read'))
  const projectTeamRoles = [api.UserRole.Owner, api.UserRole.Editor, api.UserRole.Moderator]
  const isProjectTeam = project.user_role && projectTeamRoles.includes(project.user_role)
  const isNewsMasked = !isUserAllowed
  const isWaiting = news.status === api.NewsStatus.WAITING

  let hideComments = false
  if (isNewsMasked || isWaiting) {
    // Comments are hidden for masked news and draft ones
    hideComments = true
  } else if (project.comments_enabled === api.ProjectCommentsPermission.NOONE && !isProjectTeam && !isUserStaff) {
    // Also, only staff and project team can access comments when they are disabled
    hideComments = true
  }

  const canUserEdit = useDisplayMode(project) === 'edit' && !model.project.refused(project)

  return (
    <Styles.Main>
      <Anchor id="anchor" isPreview={isPreview} />
      <Styles.Wrapper>
        <Styles.Content>
          <BackToNewsStyled forwardedAs={RouterLink} to={navigation.pathTo(PATHS.NEWS)}>
            <plume.glyphs.stroke.ArrowLeft size={15} />
            {t('See all posts')}
          </BackToNewsStyled>

          <NewsContent
            isEditMode={canUserEdit}
            project={project}
            news={news}
            lockedCTA={
              news.audience_type === api.NewsAudienceType.FANS ? (
                <FollowProjectButton source={TRACKING_SOURCE.NEWS} shouldReloadPage={true} />
              ) : (
                <plume.RoundButton
                  onClick={() => {
                    navigation.to(PATHS.REWARDS)
                  }}
                >
                  {t('Contribute')}
                </plume.RoundButton>
              )
            }
          />

          {!hideComments && (
            <CommentBox>
              <duvet.comment.CommentBox isCommentingAllowed objectId={news.id} />
            </CommentBox>
          )}
        </Styles.Content>
        <Styles.Sidebar isEditMode={canUserEdit}>
          {model.project.isDonationBased(project) ? <SidebarDonationCTA /> : <SidebarRewardsCTA />}
          {<SidebarNewsCTA source={TRACKING_SOURCE.NEWS} />}
        </Styles.Sidebar>
      </Styles.Wrapper>
    </Styles.Main>
  )
}

const NewsContent = styled(duvet.NewsContent)`
  margin-bottom: 24px;
`

const BackToNewsStyled = styled(plume.Link).attrs((props) => ({ ...props, kind: 'secondary' }))`
  display: block;
  margin-bottom: 31px;

  ${plume.glyphs.stroke.ArrowLeft} {
    margin-right: 8px;
  }
`

const Anchor = styled.div<{ isPreview: boolean }>`
  position: absolute;
  top: ${({ isPreview }) => (isPreview ? '-108px' : '-60px')};
`

const CommentBox = styled.section`
  padding: 16px 0 0;
  margin-bottom: -12px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    background-color: ${plume.COLORS.PRIMARY_GREY_000};
    border-radius: 4px;
    box-shadow: ${plume.EFFECTS.SHADOW_5};
    padding: 32px;
    margin-bottom: 0;
  }
`
